/**
  * @class meleMap
  * @description
  *
  * # Purpose
  * Implements `<mele-map>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-map></mele-map>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-map',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  * item structure is as follows:
  *  item = {
  *    key: 'abc',
  *    marker: 'unit',
  *    shape: 'P,u2s1yhfygjhk,u2s1yh6rtmf5,u2s1yh6qehkc,u2s1yh6jrg8t,u2s1yh6hwj2z,u2s1y5cz92re',
  *    pane: 'selected', // if not set, will be assigned automatically depending on type
  *    data: { ... custom data store ... }
  *  }
  *
  */

import msysListener from 'msysListener'
import meleView from 'meleView'
import meleMap_dot from './shapes/meleMap_dot.js'
import meleMap_circle from './shapes/meleMap_circle.js'
import meleMap_rectangle from './shapes/meleMap_rectangle.js'
import meleMap_square from './shapes/meleMap_square.js'
import meleMap_path from './shapes/meleMap_path.js'
import meleMap_area from './shapes/meleMap_area.js'
import ResizeObserver from 'resize-observer-polyfill'
const geo = require('msysGeo')
const melemap_lib = require('./meleMap_lib.js')

const PROPERTIES = _.extend({}, {
  base: { type: 'string', description: 'base map name' },
  items: { type: 'object', description: 'child items', noattribute: true },
  selected: { type: 'array', description: 'list of keys of selected items' },

  maxzoom: { type: 'number', description: 'Max zoom of the view', default: 16 },
  zoom: { type: 'number', description: 'Current zoom level', default: 13 },
  minzoom: { type: 'number', description: 'Min zoom of the view', default: 6 },
  fitzoom: { type: 'number', description: 'Maximum fit zoom of the view', default: 15 },
  center: { type: 'string', description: 'Map center as geohash', default: 'u2s1yh4dzj87' },

  searchbar: { type: 'boolean', description: 'whether to show search bar' },
  searchlist: { type: 'boolean', description: 'whether to show search list' },
  search: { type: 'string', description: 'content of search bar' },
  searchlimit: { type: 'integer', description: 'maximum number of displayed search results', default: 30 },

  layerbutton: { type: 'boolean', description: 'whether to show layers button' },
  layerlist: { type: 'boolean', description: 'whether to show layers list', default: false },
  layeroptions: { type: 'array', description: 'which layers are available for selection', default: ['unit', 'incident', 'instruction'] },
  layers: { type: 'string', description: 'which layers are displayed; space separated list' },

  shapebutton: { type: 'boolean', description: 'whether to show shape selection button' },
  shapelist: { type: 'boolean', description: 'whether to show shape selection list' },
  shape: { type: 'string', description: 'which shape is selected for drawing', enum: ['', 'D', 'C', 'R', 'S', 'P', 'A'] },

  gpsbutton: { type: 'boolean', description: 'whether to show button to show my location' },
  gps: { type: 'string', description: 'my location in format: geohash/rotation' },

  eventDraw: { type: 'function', description: 'called when manual drawing changed' },
  eventSelected: { type: 'function', description: 'method that can be overwritten to inform about change of selected items' },
  eventDoubleClicked: { type: 'function', description: 'method that can be overwritten to inform about doubleclick on item' },
}, meleView._properties)

// preparation of custom tile layer that gathers tile pictures using mmFileGet
L.GridLayer.Mosy = L.GridLayer.extend({
  createTile: function(coords, done) {
    const tile = document.createElementFast('img')
    tile.setAttribute('x', coords.x)
    tile.setAttribute('y', coords.y)
    tile.setAttribute('z', coords.z)

    const elMap = this.getContainer().closest('.mele-map')
    let base = _.toString(elMap.base)
    if(base=='') {
      const elContainer = elMap ? elMap.closest('.mele-window,.mele-modal') : null
      if(elContainer && elContainer.theme) base = _.toString(elContainer.theme)
      else base='day'
    }
    const uri = 'map_'+base+'/'+coords.z+'/'+coords.x+'/'+coords.y+'.png'

    elMap.listener.request('msysProxy', 'mmFileGet', { uri }, 'mmFile')
      .then(reply=>{
        tile.src = reply?.params?.data
        done(null, tile)
      })
      .catch(e=>{
        done(String(e))
      })
    return tile
  },
  getAttribution: function() { return '' },
})

L.gridLayer.mosy = function() {
  return new L.GridLayer.Mosy()
}

export default class meleMap extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this._items={}

    // create a listener to receive messsages from msysProxy
    this.listener = new msysListener(0)
    this.listener.publish('#ME', 'mmStart')

    // create leaflet map
    this._map = L.map(this._body, {
      center: melemap_lib.geohash2point(this.center),
      zoom: this.zoom || PROPERTIES.zoom.default,
      minZoom: this.minzoom || PROPERTIES.minzoom.default,
      maxZoom: this.maxzoom || PROPERTIES.maxzoom.default,
      zoomControl: false,
      attributionControl: false,
      tap: true,
    })

    this._resizeObserver = new ResizeObserver(()=>{
      this._map.invalidateSize()
    }).observe(this._body)

    this._map.on('tileerror', (error, tile)=>{
      console.error([error, tile])
      this.unavailable = true
    })

    this._map.on('click', event=>{
      if(!event.originalEvent) return
      this.selected = []
    })
    this._map.on('move', (event) => {
      if(!event.originalEvent) return // skip update if the event was not performed by user interaction
      this.selected = []

      const center = this._map.getCenter()
      this.setProperty('center', melemap_lib.point2geohash(center), true)
    })
    this._map.on('zoom', (event) => {
      // if(!event.originalEvent) return; // skip update if the event was not performed by user interaction
      this.setProperty('zoom', this._map.getZoom(), true)
    })

    setInterval(() => {
      // periodically ask leaflet to recalculate container size
      this._map.invalidateSize()
    }, 1000)

    // create layers for placing our shapes and markers
    this._mainLayer = L.gridLayer.mosy().addTo(this._map)
    //  this._mainLayer = L.tileLayer(TILE_LAYERS.day, { maxZoom: this.maxzoom }).addTo(this._map);
    this._map.createPane(melemap_lib.DEFAULT_PANE)
    this._map.createPane(melemap_lib.SEARCH_PANE)
    this._map.createPane(melemap_lib.DRAW_PANE)

    // create layer button
    this._layerbutton = this._body_wrapper.appendChildren({
      key: 'layerbutton',
      tag: 'mele-button',
      type: 'toolbar',
      iconleft: 'map.layers',
      eventButtonClicked: ()=>{
        this.layerlist = !this.layerlist
      },
    })

    // create layer selector
    this._layerlist = this._body_wrapper.appendChildren({
      key: 'layerlist',
      tag: 'mele-list',
      direction: 'row',
      prefix: 'map.icon.',
      multi: true,
      iconleft: 'map.icon.${key}',
      tooltip: 'map.icon.${key}',
      eventSelected: (el, selected) => { this.layers = selected.join(' ') },
    })
    const layer_items = []
    for(const item of this.layeroptions) {
      layer_items.push({ key: item })
    }
    this._layerlist.setItems(layer_items)

    // create drawing toolbar
    this._shapelist = this._body_wrapper.appendChildren({
      key: 'shapelist',
      tag: 'mele-list',
      direction: 'row',
      design: 'icongroup',
      prefix: 'map.shape.',
      blank: true,
      auto: true,
      iconleft: 'map.shape.${key}',
      selected: [''],
      eventIconClicked: (el, item)=>{
        setTimeout(()=>{
          this.shapelist=!this.shapelist
          this.shape = item.key
        }, 10)
      },
    })

    // create GPS button
    this._gpsbutton = this._body_wrapper.appendChildren({
      key: 'gpsbutton',
      tag: 'mele-button',
      type: 'toolbar',
      iconleft: 'map.gps',
      eventButtonClicked: ()=>{
        this.selected=[]
        this.selected=['gps']
        this.zoom=this.maxzoom
      },
    })
  } // init

  /* ****************************************************************************************************************** */
  /*                                      UTILITY METHODS                                                               */
  /* ****************************************************************************************************************** */

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * force redraw main layer
  */
  redraw() {
    if(this._mainLayer) {
      this._mainLayer.redraw()
    }
  } // redraw

  /* ****************************************************************************************************************** */
  /*                      HANDLING CHANGES ON ELEMENT ATTRIBUTES                                                        */
  /* ****************************************************************************************************************** */

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change of base tile layer
  */
  baseChanged() {
    this._mainLayer.redraw()
  } // baseChanged

  searchbarChanged(value) {
    if(value) {
      this._searchlist = this._body_wrapper.appendChildren({
        key: 'searchlist',
        tag: 'mele-search',
        tabs: 'map.icon.',
        tab: '',
        eventSelected: (el, selected)=>{ this.searchSelected(el, selected) },
        eventSearch: (el, search)=>{ this.search = search },
      })
    } else {
      if(this._searchlist) {
        this._searchlist.deleteElement()
        delete this._searchlist
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change in disablebility
  */
  disabledChanged() {
    if(this.disabled) {
      this._map.dragging.disable()
      this._map.touchZoom.disable()
      this._map.doubleClickZoom.disable()
      this._map.scrollWheelZoom.disable()
      this._map.boxZoom.disable()
      this._map.keyboard.disable()
      this._map.fadeAnimation = false
      this._map.zoomAnimation = false
      if(this._map.tap) this._map.tap.disable()
    }else {
      this._map.dragging.enable()
      this._map.touchZoom.enable()
      this._map.doubleClickZoom.enable()
      this._map.scrollWheelZoom.enable()
      this._map.boxZoom.enable()
      this._map.keyboard.enable()
      this._map.fadeAnimation = true
      this._map.zoomAnimation = false
      if(this._map.tap) this._map.tap.enable()
    }
  } // disabledChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change of minimum zoom
  */
  minzoomChanged(value) {
    this._map.setMinZoom(this.minzoom)
  } // minzoomChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change of maximum zoom
  */
  maxzoomChanged(value) {
    this._map.setMaxZoom(this.maxzoom)
  } // maxzoomChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change of zoom
  */
  zoomChanged(value, old, context_user_interaction) {
    if(context_user_interaction) return
    this.info('zoomChanged', value, old)
    this._map.setView(melemap_lib.geohash2point(this.center), this.zoom)
  } // zoomChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change of map center
  */
  centerChanged(value, old, context_user_interaction) {
    if(value === '7zzzzzzzzzzz' || context_user_interaction) return
    const holder = this.closest('mele-window')
    const focused = !holder || holder.focused
    if(value=='') value = meleMap._properties.center.default
    if(focused) {
      setTimeout(()=>{
        this._map.panTo(melemap_lib.geohash2point(value))
      }, 200)
    }
  } // centerChanged

  eventWindowFocused() {
    setTimeout(()=>{
      this._map.panTo(melemap_lib.geohash2point(this.center))
    }, 200)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "layers"
  */
  layersChanged(value) {
    let layers = _.toString(value).splitCorrect(/\s+/)
    layers = _.uniq(layers)
    if(this._layerlist && !_.isEqual(_.arrayToHash(layers), _.arrayToHash(this._layerlist.selected))) {
      this._layerlist.selected = layers
    }
  } //layersChanged

  layerlistChanged(value) {
    this.info('layerlistChanged', value)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "shape"
  */
  shapeChanged(value) {
    this._shapelist.selected = [value]
    if(value) {
      this.startDrawing(value)
    }else {
      this.stopDrawing()
    }
  } // shapeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "gps"
  */
  gpsChanged(value) {
    value = _.toString(value).splitCorrect('/')
    if(value.length==0) {
      this.deleteItem('gps')
    }else{
      this.setItem({
        key: 'gps',
        marker: 'gps',
        shape: value[0],
        rotate: parseInt(value[1]),
      })
    // if(this.selected.indexOf('gps'))
    //   this.center = value[0];
    }
  } // gpsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Tries to put mp to zoom level and position so that it shows all map items
  */
  fitItems(fromObserver) {
    const elBody = this._body
    if(!(elBody.clientHeight>0)) {
      if(fromObserver) return
      let observer = new ResizeObserver(()=>{
        this.fitItems(true)
        if(observer) observer.disconnect()
        observer = null
      }).observe(elBody)
      return
    }

    this._map.invalidateSize()
    const fit_bounds = L.latLngBounds()
    _.values(this._items).forEach(item=>{
      const bounds = item.bounds()
      if(bounds) fit_bounds.extend(bounds)
    })
    try{
      const max_fit_zoom = this.fitzoom || PROPERTIES.fitzoom.default
      if(this._map.getZoom()<max_fit_zoom) this._map.setZoom(max_fit_zoom, { animate: false })
      this._map.fitBounds(fit_bounds, { maxZoom: max_fit_zoom })
      setTimeout(()=>{
        const center = this._map.getCenter()
        this.setProperty('center', melemap_lib.point2geohash(center), true)
      }, 1000)
    }catch(e) {}
  } // fitItems

  /* ****************************************************************************************************************** */
  /*                      ITEM MANAGEMENT                                                                               */
  /* ****************************************************************************************************************** */

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * called by object item when user clicks on it
  */
  itemEvent(event, action, key) {
    if(this.shape) return
    event.stopPropagation()
    if(action=='click') {
      this.selected = [key]
      if(this.searchlist && this._searchlist) {
        this._searchlist.selected = [key]
      }
    }else if(action=='dblclick') {
      if(key && typeof (this.eventDoubleClicked)==='function') {
        try{
          this.eventDoubleClicked(this, key)
        }catch(e) {}
      }else{
        this.zoom=15
      }
    }
  } // itemSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * handles change of selected items
  */
  selectedChanged(value, old, context_user_interaction) {
    const selected = _.arrayToHash(value)
    for(const key in this._items) {
      this._items[key].select(selected[key])
    }
    if(value.length==1) {
      const item = this._items[value[0]]
      if(item) {
        const center = item.center()
        if(center) this.center = center
      }
    }
    this.eventSelected(this, value, old, context_user_interaction)
    if(this.closest('.mele-window,.mele-modal').behavior=='phone') {
      this.searchlist = false
    }
  } // selectedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * default handling of eventSelected
  */
  eventSelected(el, value, old, context_user_interaction) {
    if(context_user_interaction) {
      this.publish('mmMapSelected', { holder: this.key, selected: value.join('∫'), old: old.join('∫') })
    }
  } // eventSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * default handling of eventDoubleClicked
  */
  eventDoubleClicked(el, key) {
  } // eventDoubleClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * getItmes() - returns all items on map
  * getItems(keys) - return all items with given keys
  */
  getItems(keys) {
    if(_.isNil(keys)) { keys = _.keys(this._items) }
    if(typeof (keys)==='string' || typeof (keys)==='number') { keys = [keys] }
    if(!Array.isArray(keys)) return []

    return keys.map(key=>{
      const item = this._items[key]
      return item ? item.js : null
    })
  } // getItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * getItem(key) - return item with given key
  */
  getItem(key) {
    const items = this.getItems(_.toString(key))
    return items.length>0 ? items[0] : null
  } // getItem

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * deleteItems() - renove all items of given type
  * deleteItems(keys) - remove all items of given keys and type
  */
  deleteItems(keys) {
    if(_.isNil(keys)) { keys = _.keys(this._items) }
    if(typeof (keys)==='string' || typeof (keys)==='number') { keys = [keys] }
    if(!Array.isArray(keys)) return
    keys.forEach(key=>{
      const item = this._items[key]
      if(item) {
        item.destroy()
        delete this._items[key]
      }
    })
  } // deleteItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * deleteItem(type, key) - remove item with given key and type
  */
  deleteItem(key) {
    this.deleteItems([key])
  } // deleteItem

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * setItems([{type:...,key:...}, ...]) - replaces all map items with new list
  */
  setItems(items) {
    this.updateItems(items)
  } // setItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * updateItems([{type:...,key:...}, ...]) - inserts or replace map items
  */
  updateItems(items) {
    items = Array.isArray(items) ? items : _.isObjectLike(items) ? [items] : []
    if(!this._values.items) { this._values.items = {} }
    items.forEach(item=>this.setItem(item))
  } // updateItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * setItem({type:...,key:...}) - replace or create if not exists map item
  */
  setItem(item, silent) {
    if(!_.isObjectLike(item)) return
    item = _.cloneDeep(item)
    let key = item.key = _.toString(item.key)
    if(key=='' && _.toString(item.id)!='') {
      key = item.key = _.toString(item.marker+':'+item.id)
    }
    let shape = item.shape = _.toString(item.shape) || _.toString(item.center)
    item.data = _.isObjectLike(item.data) ? item.data : {}
    let shape_type

    // identify shape to be used
    const m = _.toString(shape).match(/^(\w),/)
    if(m) {
      shape_type = m[1]
    }else if(!m) {
      shape_type = 'D'
      shape = 'D,' + _.toString(item.geo)
    }

    // destroy if existing item is of different shape
    const items = this._items
    let existing = this._items[key]
    if(existing && existing.constructor.shape()!=shape_type) {
      existing.destroy()
      items[key] = existing = null
    }

    // create if missing
    if(!existing) {
      items[key] = existing =
      shape_type=='C' ? new meleMap_circle(this._map, null, this.itemEvent.bind(this)) :
      // shape_type=='E' ? new meleMap_ellipse(this._map, null, this.itemEvent.bind(this)) :
        shape_type=='S' ? new meleMap_square(this._map, null, this.itemEvent.bind(this)) :
          shape_type=='R' ? new meleMap_rectangle(this._map, null, this.itemEvent.bind(this)) :
            shape_type=='P' ? new meleMap_path(this._map, null, this.itemEvent.bind(this)) :
              shape_type=='A' ? new meleMap_area(this._map, null, this.itemEvent.bind(this)) :
              // shape_type=='p' ? new meleMap_freepath(this._map, null, this.itemEvent.bind(this)) :
              // shape_type=='a' ? new meleMap_freearea(this._map, null, this.itemEvent.bind(this)) :
                new meleMap_dot(this._map, null, this.itemEvent.bind(this))
    }

    // update properties
    existing.update(item, silent)

    // center map around this item if it is selected
    if(this.selected.length==1 && this.selected[0]==key) {
      const center = existing.center()
      if(center) this.center = center
      existing.select(true)
    }

    return existing
  } // setItem

  /* ****************************************************************************************************************** */
  /*                      SEARCHING                                                                                     */
  /* ****************************************************************************************************************** */

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "search"
  */
  searchChanged(search) {
    search = _.toString(search)
    if(this._searchlist && this._searchlist.search != search) {
      this._searchlist.search = search
    }

    // remove temporary search items from previous search and move permanent items back to default pane
    _.each(this._items, (item, key)=>{
      if(item._props.search_temporary) {
        item.destroy()
        delete this._items[key]
      }
    })

    // call either external search method or internal search within map items
    // both report back the results by calling .updateSearch()
    if(typeof this.eventSearch === 'function') {
      this.eventSearch(this, search)
    }else{
      this.searchInMapItems(search)
    }
  } // searchChanged

  updateSearch(search, items) {
    // forward update results to searchlist
    const current_search = this._searchlist.search
    if(!this._searchlist && !Array.isArray(items) || current_search != search) return // search field changed before the response came
    this._searchlist.updateSearch(search, items)

    // draw search results on map
    items.forEach(item=>{
      const key = _.toString(item.key)
      if(key=='') return

      // for permanent items on map we just update them with search result and force their move to search pane
      // for new items we mark them as temporary for search and set them to search pane
      const existing = this._items[key]
      item = _.extend({}, existing?existing._props:{}, item, { pane: melemap_lib.SEARCH_PANE, search_temporary: !existing })
      this.setItem(item)
    })
  }

  searchSelected(el, selected) {
    this.selected = selected
    const item = this._items[selected[0]]
    if(item) {
      this.center = item.center()
    }
  }

  // perform internal search over map items
  searchInMapItems(search) {
    if(search=='') return this.updateSearch(search, [])

    const domains = {}

    // search other items presented on the map
    const stringTerms = function(txt) {
      txt = LANG.removeAccents(_.toString(txt)).toLowerCase()
      return txt.split(/[\\x00-\\x20]+/).filter(term=>term.length>0)
    }
    const search_terms = stringTerms(search)
    for(const key in this._items) {
      const [domain] = String(key).split(':')
      if(!domain || domain=='place') continue
      if(domains[domain] && domains[domain].length>=this.searchlimit) continue
      const item = this._items[key]
      const props = item._props || {}
      const terms = stringTerms(_.toString(props.label)+' '+_.toString(props.label2))
      if(terms.length<search_terms.length) continue // item has no label/label2
      let matching = true
      for(let i=0; i<search_terms.length; i++) {
        const search_term = search_terms[i]
        const term = terms[i]
        if(term.length<search_term.length || term.substr(0, search_term.length)!=search_term) {
          matching = false
          break
        }
      }
      if(!matching) continue

      domains[domain] = domains[domain] || []
      props.tab = domain
      props.iconright = props.iconright || 'map.icon.'+domain
      domains[domain].push(props)
    }

    // search addresses
    if(search.length>0) {
      let list = geo.search(search)
      if(!Array.isArray(list)) list = []
      if(list.length > this.searchlimit) list = list.slice(0, this.searchlimit)
      domains.place = this.transformPlacesToSearchItems(list)
    }

    // limit results
    let results = []
    for(const d in domains) {
      results = results.concat(domains[d])
    }

    this.updateSearch(search, results)
  } // searchInMapItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method to transform list of places into search items
  */
  transformPlacesToSearchItems(items) {
    if(!Array.isArray(items)) return
    items = items
      .map(item=>{
        if(!_.isObjectLike(item)) return
        const out = {
          tab: 'place',
          key: 'place:'+item.id,
          shape: item.shape || item.id.replace(/\W.*/, ''),
          data: item,
        }
        const type = _.toString(item.type) || 'address'
        if(type=='address') {
          out.label = _.joinStrings(' ', item.street, item.streetno)
          out.label2 = _.joinStrings(' ', item.zip, item.city)
          out.marker = 'address'
          out.iconright = 'marker.marker'
        }else{
          out.label = _.toString(item.name)
          out.label2 = _.joinStrings(', ', _.joinStrings(' ', item.street, item.streetno), _.joinStrings(' ', item.zip, item.city))
          out.marker = 'marker'
          out.iconright = 'map.pin.add'
        }
        return out
      })
      .filter(item=>item && item.key)
    return items
  } // transformPlacesToSearchItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method to transform list of units into search items
  */
  transformUnitsToSearchItems(items) {
    if(!Array.isArray(items)) return
    return items
      .map(item=>{
        if(!_.isObjectLike(item)) return {}
        return {
          tab: 'unit',
          key: 'unit:'+item.id,
          label: _.toString(item.callsign),
          label2: _.toString(item.type),
          color: _.colorFromFirstCharacter(_.toString(item.status)),
          iconright: 'mmodUnits',
          shape: _.toString(item.gps),
          marker: 'unit',
          data: item,
        }
      })
      .filter(item=>item.key)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * helper method to transform list of copanies into search items
  */
  transformCompaniesToSearchItems(items) {
    if(!Array.isArray(items)) return
    return items
      .map(item=>{
        if(!_.isObjectLike(item)) return {}
        return item
      })
      .filter(item=>item.id)
  } // transformCompaniesToSearchItems

  /* ****************************************************************************************************************** */
  /*                      DRAWING                                                                                       */
  /* ****************************************************************************************************************** */

  startDrawing(shape) {
    this.stopDrawing()
    this.selected=['drawing']
    this._drawing = this.setItem({
      key: 'drawing',
      marker: 'drawing',
      pane: melemap_lib.DRAW_PANE,
      shape: shape+',',
    })

    this._map.on('click', (event)=>{
      this._drawing.addPoint(event.latlng)
    })
    this._map.on('mousemove', (event)=>{
      this._drawing.guide(event.latlng)
    })
  } // startDrawing

  stopDrawing() {
    if(this._drawing) { this._drawing.destroy() }
    delete this._drawing
    this._map.off('click')
    this._map.off('mousemove')
  } // stopDrawing
} // class meleMap

meleMap._properties = PROPERTIES
meleMap.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-map', meleMap)
